import type { AutoConnection, GraphDocument } from '../../../core/entities/graph-document';
import type { JourneyNode, NodeType } from '../../../core/entities/node';
import type { WorkspaceGraphMode, WorkspaceMode } from '../../../core/ports/preferences-repository';
import { focusedEdgeEmphasis, visibleNodes } from '../../../core/policies/search/focus-visualization';
import { getWorkspaceEditLock, type WorkspaceEditLockMap } from '../../../core/policies/workspace/edit-lock';
import {
	resolveSceneInteractionMode,
	type SceneInteractionMode,
} from '../../../core/policies/workspace/scene-interaction-mode';

export interface WorkspaceGraphSurface {
	interactive3D: boolean;
	show2DCanvas: boolean;
	show3DScene: boolean;
}

export interface GraphFocusContext {
	focusedNode: JourneyNode | null;
	focusedNodeLockedByOutline: boolean;
	highlighted: AutoConnection[];
}

export function selectWorkspaceGraphSurface(
	currentMode: WorkspaceMode,
	currentGraphMode: WorkspaceGraphMode,
): WorkspaceGraphSurface {
	return {
		interactive3D: currentMode === '3d',
		show2DCanvas: currentMode === '2d' || (currentMode === 'outline' && currentGraphMode === '2d'),
		show3DScene: currentMode === '3d' || (currentMode === 'outline' && currentGraphMode === '3d'),
	};
}

export function selectGraphFocusContext({
	activeEditLocks = {},
	focusedNodeId,
	graph,
	visibleType,
}: {
	activeEditLocks?: WorkspaceEditLockMap;
	focusedNodeId: string | null;
	graph: GraphDocument;
	visibleType: NodeType | 'All';
}): GraphFocusContext {
	const focusedNode = selectFocusedNode(graph, visibleType, focusedNodeId);
	const focusedNodeLock = focusedNodeId ? getWorkspaceEditLock(activeEditLocks, focusedNodeId) : null;

	return {
		focusedNode,
		focusedNodeLockedByOutline: focusedNodeLock?.owner === 'outline-inline',
		highlighted: focusedNodeId ? focusedEdgeEmphasis(graph.connections, focusedNodeId) : [],
	};
}

export function selectSceneInteractionModeForWorkspace({
	currentGraphMode,
	currentMode,
	readonlyMode,
	requestedMode,
}: {
	currentGraphMode: WorkspaceGraphMode;
	currentMode: WorkspaceMode;
	readonlyMode: boolean;
	requestedMode: SceneInteractionMode;
}): SceneInteractionMode {
	const graphSurface = selectWorkspaceGraphSurface(currentMode, currentGraphMode);

	return resolveSceneInteractionMode(graphSurface.interactive3D ? requestedMode : 'navigate', {
		readonlyMode,
	});
}

function selectFocusedNode(
	graph: GraphDocument,
	visibleType: NodeType | 'All',
	focusedNodeId: string | null,
): JourneyNode | null {
	if (!focusedNodeId) {
		return null;
	}

	const nodes = visibleNodes(graph.nodes, visibleType);
	return nodes.find((node) => node.nodeId === focusedNodeId) ?? null;
}